import React from 'react';
import { BADGES, getBadgeRarityColor } from '../constants/gamification';
import { Award, Lock } from 'lucide-react';

interface BadgesDisplayProps {
  earnedBadgeIds: string[];
  compact?: boolean; // For dashboard display
}

export function BadgesDisplay({ earnedBadgeIds, compact = false }: BadgesDisplayProps) {
  const earnedBadges = BADGES.filter((badge) => earnedBadgeIds.includes(badge.id));
  const lockedBadges = BADGES.filter((badge) => !earnedBadgeIds.includes(badge.id));

  if (compact) {
    // Compact display for dashboard - show only top 3 badges
    const topBadges = earnedBadges.slice(0, 3);

    if (topBadges.length === 0) {
      return (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Award className="w-4 h-4" />
          <span>No badges yet - start saving food!</span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {topBadges.map((badge) => (
          <div
            key={badge.id}
            className="flex items-center gap-2 bg-white border-2 rounded-lg px-3 py-1.5 shadow-sm"
            style={{ borderColor: getBadgeRarityColor(badge.rarity) }}
            title={badge.description}
          >
            <span className="text-2xl">{badge.icon}</span>
            <div>
              <p className="text-xs font-bold text-gray-900">{badge.name}</p>
              <p className="text-xs text-gray-500 capitalize">{badge.rarity}</p>
            </div>
          </div>
        ))}
        {earnedBadges.length > 3 && (
          <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm font-semibold text-gray-600">
            +{earnedBadges.length - 3} more
          </div>
        )}
      </div>
    );
  }

  // Full display for profile/dedicated page
  return (
    <div>
      {/* Earned Badges */}
      {earnedBadges.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-primary-green" />
            Your Badges ({earnedBadges.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {earnedBadges.map((badge) => (
              <div
                key={badge.id}
                className="bg-white border-2 rounded-xl p-4 shadow-md hover:shadow-lg transition-all"
                style={{ borderColor: getBadgeRarityColor(badge.rarity) }}
              >
                <div className="text-center">
                  <span className="text-5xl mb-3 block">{badge.icon}</span>
                  <h4 className="font-bold text-gray-900 mb-1">{badge.name}</h4>
                  <p
                    className="text-xs font-semibold uppercase mb-2"
                    style={{ color: getBadgeRarityColor(badge.rarity) }}
                  >
                    {badge.rarity}
                  </p>
                  <p className="text-xs text-gray-600">{badge.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Locked Badges */}
      {lockedBadges.length > 0 && (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-gray-400" />
            Locked Badges ({lockedBadges.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {lockedBadges.map((badge) => (
              <div
                key={badge.id}
                className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 opacity-60"
              >
                <div className="text-center">
                  <span className="text-5xl mb-3 block grayscale opacity-40">{badge.icon}</span>
                  <h4 className="font-bold text-gray-700 mb-1">{badge.name}</h4>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                    {badge.rarity}
                  </p>
                  <p className="text-xs text-gray-600">{badge.description}</p>
                  <div className="mt-3 bg-gray-200 rounded-full h-2">
                    <div className="bg-gray-400 h-2 rounded-full" style={{ width: '0%' }}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {earnedBadges.length === 0 && lockedBadges.length === 0 && (
        <div className="text-center py-12">
          <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No badges available yet</p>
        </div>
      )}
    </div>
  );
}
